<template>
  <div class="table-chart">
    <div ref="chartTable" class="table-chart__container mb-16">
      <h3
        v-if="title"
        class="table-chart__title mt-0 mb-16">
        {{ title }}
      </h3>

      <table
        v-if="tableColumns && limitedResult"
        class="table-chart__table"
        :class="`table-chart__table--col-${tableColumns.length}`">
        <thead>
          <tr>
            <th
              :key="column"
              v-for="column in tableColumns">
              <div class="table-chart__th-wrapper">
                {{ column }}
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            :key="`row-${y}`"
            v-for="(row, y) in limitedResult">
            <td
              :key="`column-${y}-${x}`"
              v-for="(value, x) in row"
              >
              <div class="table-chart__td-wrapper">
                <span
                  class="table-chart__null-value"
                  v-if="value === null">NULL</span>
                <span
                  v-else>{{ value }}</span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="table-chart__actions">
      <div class="table-chart__note">
        * only the first {{ maxTableRows }} rows are displayed
      </div>

      <a
        href="#"
        ref="downloadImageLink"
        class="btn btn--secondary btn--xs"
        @click.prevent="downloadImage">Download image</a>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import html2canvas from "html2canvas";

const MAX_TABLE_ROWS = 30;

export default {
  name: "TableChart",
  data() {
    return {
      chart: null,
      maxTableRows: MAX_TABLE_ROWS
    }
  },
  computed: {
    ...mapGetters("chart", ["title", "tableColumns", "fields", "result"]),
    limitedResult() {
      const columnIndices = this.tableColumns.map(columnName => this.fields.findIndex(fieldName => columnName === fieldName));

      return this.result.slice(0, MAX_TABLE_ROWS).map(row => {
        return columnIndices.map(columnIndex => row[columnIndex]);
      });
    },
  },
  methods: {
    async downloadImage() {
      try {
        const canvas = await html2canvas(this.$refs.chartTable, { useCORS: true });

        canvas.toBlob((blob) => {
          if (blob) {
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = "table_chart.png";

            document.body.appendChild(link);
            link.click();

            URL.revokeObjectURL(link.href);
            document.body.removeChild(link);
          } else {
            console.error("Could not generate image blob.");
          }
        }, "image/png");
      } catch (error) {
          console.error("Error generating image:", error);
      }
    }
  }
};
</script>

<style lang="scss">
.table-chart {
  $root: &;

  &__container {
    width: 100%;
    position: relative;
  }

  &__title {
    @include h3;
    color: $grey-2;
    font-weight: 700;
  }

  &__note {
    @include font-small;
    color: $grey-4;
  }

  &__actions {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }

  &__table {
    position: relative;
    width: 100%;
    overflow-x: auto;
    overflow-y: hidden;

    border: $px1 solid $grey-9;
    border-collapse: collapse;

    font-size: 14px;

    &::-webkit-scrollbar {
      display: none;
    }

    th,
    td {
      padding: 0;
      margin: 0;
      border: 0;
    }

    tr {
      &:nth-child(even) {
        background-color: $grey-10;
      }
    }

    tbody {
      td {
        &:only-of-type {
          #{$root}__td-wrapper {
            border-right: 0;
          }
        }

        &:last-child {
          #{$root}__td-wrapper {
            border-right: 0;
          }
        }
      }
    }

    th {
      &:only-of-type {
        #{$root}__th-wrapper {
          border-right: 0;
        }
      }

      &:last-child {
        #{$root}__th-wrapper {
          border-right: 0;
        }
      }
    }

    &--col-1,
    &--col-2,
    &--col-3,
    &--col-4,
    &--col-5,
    &--col-6 {
      #{$root}__th-wrapper {
        max-width: 100%;
      }

      #{$root}__td-wrapper {
        max-width: 100%;
      }
    }
  }

  &__th-wrapper {
    padding: $px4 $px8;
    max-width: $px320;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    height: 29px; // https://stackoverflow.com/questions/7790222/css-display-table-min-height-not-working
    border-right: $px1 solid $grey-8;

    background-color: $grey-9;
  }

  &__td-wrapper {
    padding: $px4 $px8;
    max-width: $px320;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    height: 29px; // https://stackoverflow.com/questions/7790222/css-display-table-min-height-not-working

    border-right: $px1 solid $grey-9;

    text-align: left;
  }

  &__null-value {
    @include font-legal;
    color: $grey-8;
  }
}
</style>
